import { mdiContentSave, mdiKey, mdiKeyRemove } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AiIssuedKey, AiKeyMetadata } from '../../api';
import { envFromBaseUrl } from '../../services/ai/ai.derive';
import { validateConfiguration } from '../../services/ai/ai.form';
// Through the services barrel, as the other UI that reads AuthContext does (metrics-bar). Importing
// `services/auth/auth.provider` directly enters that module out of order: it sits in a cycle with `ui` (auth.service
// imports the data-table), and its own barrel then finishes before AuthContext exists, which fails at startup with
// "Cannot access 'AuthContext' before initialization".
import { AuthContext, ToastContext, useUnsavedChangesWarning } from '../../services';
import { AiBannerAction, AiStore } from '../../stores/ai/ai.store';
import { BaseButton } from '../base/button/button';
import { formatWhen } from './ai-fields';
import { CatalogTab } from './catalog-tab';
import { ConfigTab } from './config-tab';
import { ConfirmEmptyDialog, IssueKeyDialog, KeySecretDialog, RevokeKeysDialog } from './dialogs';
import { KeysTab } from './keys-tab';
import { ProfileDrawer } from './profile-drawer';
import { ProfilesTab } from './profiles-tab';
import { TestingTab } from './testing-tab';
import * as S from './ai.styles';

type TabId = 'config' | 'profiles' | 'keys' | 'test' | 'catalog';

type DialogState =
    | { kind: 'issue' }
    | { kind: 'secret'; issued: AiIssuedKey }
    | { kind: 'revoke'; scope: string }
    | { kind: 'confirmEmpty'; count: number };

export interface IAiScreenProps {
    /** The app's AI store: `createAiStore('support')` or `createAiStore('provider')`. It decides what the screen offers. */
    store: AiStore;
}

/** Re-render countdowns and expiry once in a while; a key expires with no request to say so. */
const TICK_MS = 15000;

const AI_FETCH_GROUP = 'ai-admin';

/**
 * The AI page, shared by the support app (configure the account's AI policy, issue and revoke keys, try the gateway)
 * and the provider app (see the profiles on offer, issue and revoke its own keys, try the gateway). What differs is
 * read from `store.capabilities`, so the two apps cannot drift apart screen by screen.
 */
export const AiScreen = observer(({ store }: IAiScreenProps): React.ReactElement => {
    const toast = useContext(ToastContext);
    const { configure, catalog, bulkRevoke } = store.capabilities;
    const [tab, setTab] = useState<TabId>(configure ? 'config' : 'profiles');
    const [dialog, setDialog] = useState<DialogState | null>(null);
    const [editing, setEditing] = useState<number | null>(null);

    // Held in a ref so the effect below runs once per page visit, not on every render of the auth context.
    const auth = useContext(AuthContext);
    const authRef = useRef(auth);
    authRef.current = auth;

    useEffect(() => {
        // The app's global error handler stays on: a failed revocation (network, 403, 404) must not go unseen. The
        // group is app-wide and sticky, so it is put back exactly as it was when leaving the page.
        const previousGroup = authRef.current.group ?? '';
        authRef.current.setGroup(AI_FETCH_GROUP, {});

        void store.load();
        const timer = setInterval(store.tick, TICK_MS);
        return () => {
            clearInterval(timer);
            authRef.current.setGroup(previousGroup, {});
            // `cancelGroup(AI_FETCH_GROUP)` is deliberately NOT called. Aborting would cut the client off from a
            // key-issue request that is already out, but the server creates the key anyway: its secret and ID would
            // be lost and a live credential left with no owner. Instead the store drops every completion that belongs
            // to the page we are leaving (its generation token, bumped by `reset()`), and revokes a key that arrives
            // after the page closed. See `AiStore.isStale`.
            // Drops the plaintext keys held for the Testing tab as well.
            store.reset();
        };
    }, [store]);

    const env = envFromBaseUrl(store.baseUrl);
    const errors = store.draft ? validateConfiguration(store.draft, store.agents) : undefined;
    const issueCount = errors?.count ?? 0;
    const dirty = store.dirty;
    // Same prompt as the Workers configuration page: leaving to another page, or closing or refreshing the tab,
    // with an unsaved draft asks first. Switching between the AI page's own tabs is not leaving, so it never asks.
    // (Provider mode has no draft, so it is never dirty and never asks.)
    // The profile drawer holds its form in its own state until Apply, so `dirty` cannot see edits made there; it
    // reports them, and they count too.
    const [drawerChanged, setDrawerChanged] = useState(false);
    useUnsavedChangesWarning(dirty || drawerChanged);
    const canSave = dirty && !!store.draft && issueCount === 0 && !store.saving;
    const configTab = tab === 'config';
    const demo = store.loadState === 'demo';

    const doSave = async (): Promise<void> => {
        const res = await store.save();
        if (res.needsConfirmEmpty) setDialog({ kind: 'confirmEmpty', count: store.saved?.profiles.length ?? 0 });
        else if (res.ok) toast.notify?.('Configuration saved · effective for new keys', 'right', 'dark');
    };

    const onBannerAction = async (kind: AiBannerAction): Promise<void> => {
        if (kind === 'revoke') {
            const scope = store.banner?.revokeScope ?? '';
            store.dismissBanner();
            setTab('keys');
            setDialog({ kind: 'revoke', scope });
            return;
        }
        // Reload replaces the draft too, so an open drawer would point at a profile that may no longer be there.
        if (kind === 'reload') setEditing(null);
        await store.bannerAction(kind);
        if (kind !== 'reload' && !store.banner && !store.dirty) {
            toast.notify?.('Configuration saved · effective for new keys', 'right', 'dark');
        }
    };

    const revokeOne = async (key: AiKeyMetadata): Promise<void> => {
        const { ok } = await store.revokeKey(key.id);
        if (ok) toast.notify?.(`Key ${key.id.slice(0, 8)} revoked`, 'right', 'dark');
    };

    const issueButton = (
        <S.Button variant="primary" onClick={(): void => setDialog({ kind: 'issue' })}>
            <S.ButtonContent>
                <Icon path={mdiKey} size={0.75} />
                Issue key
            </S.ButtonContent>
        </S.Button>
    );

    const tabs: Array<{ id: TabId; label: string; count?: number | string }> = configure
        ? [
              // A count is a claim: while the configuration is unknown it is '—', not 0 (NS-3).
              { id: 'config', label: 'Configuration', count: store.draft ? store.draft.profiles.length : '—' },
              { id: 'keys', label: 'Keys', count: store.tracked.length || undefined },
              { id: 'test', label: 'Testing' },
              ...(catalog ? [{ id: 'catalog' as TabId, label: 'Catalog' }] : []),
          ]
        : [
              { id: 'profiles', label: 'Profiles', count: store.access ? store.access.profiles.length : '—' },
              { id: 'keys', label: 'Keys', count: store.tracked.length || undefined },
              { id: 'test', label: 'Testing' },
          ];

    return (
        <S.Wrapper>
            <S.Header>
                <S.TitleBlock>
                    <S.Title>AI</S.Title>
                    <S.MetaRow>
                        {/* NS-3: until the configuration has loaded the environment is unknown, not "production". */}
                        {env ? (
                            <S.Tag tone={env === 'PRODUCTION' ? 'primary' : 'attention'}>{env}</S.Tag>
                        ) : (
                            <S.Tag tone="neutral">—</S.Tag>
                        )}
                        {store.baseUrl && <S.Mono>{store.baseUrl}</S.Mono>}
                        {configure && <span>Production and sandbox are configured independently.</span>}
                    </S.MetaRow>
                </S.TitleBlock>

                {configTab && store.draft && (
                    <S.HeaderActions>
                        {dirty && (
                            <S.DirtyLabel>
                                {issueCount ? `Unsaved · ${issueCount} issue${issueCount > 1 ? 's' : ''}` : 'Unsaved changes'}
                            </S.DirtyLabel>
                        )}
                        {/* Not while a profile drawer is open: it holds an index into the draft, and Discard swaps the draft
                            under it, so Apply would overwrite a different profile. Close the drawer first. */}
                        <S.Button variant="outlined" disabled={!dirty || editing !== null} onClick={store.discard}>
                            Discard
                        </S.Button>
                        <S.Button variant="primary" disabled={!canSave} onClick={(): void => void doSave()}>
                            <S.ButtonContent>
                                <Icon path={mdiContentSave} size={0.75} />
                                {store.saving ? 'Saving…' : store.saved ? 'Save configuration' : 'Create configuration'}
                            </S.ButtonContent>
                        </S.Button>
                    </S.HeaderActions>
                )}
                {(tab === 'keys' || tab === 'profiles') && !demo && (
                    <S.HeaderActions>
                        {bulkRevoke && tab === 'keys' && (
                            <S.Button variant="outlined" onClick={(): void => setDialog({ kind: 'revoke', scope: '' })}>
                                <S.ButtonContent>
                                    <Icon path={mdiKeyRemove} size={0.75} />
                                    Revoke keys…
                                </S.ButtonContent>
                            </S.Button>
                        )}
                        {issueButton}
                    </S.HeaderActions>
                )}
            </S.Header>

            <S.Tabs<TabId> activeTab={tab} onTabChange={setTab} tabs={tabs} />

            {store.banner && (
                <S.PageBanner
                    tone={store.banner.tone}
                    title={
                        <>
                            {store.banner.title}
                            {store.banner.code && <S.Mono> {store.banner.code}</S.Mono>}
                        </>
                    }
                    action={
                        store.banner.actions.length ? (
                            <>
                                {store.banner.actions.map(a => (
                                    <BaseButton
                                        key={a.kind}
                                        variant="outlined"
                                        size="compact"
                                        onClick={(): void => void onBannerAction(a.kind)}
                                    >
                                        {a.label}
                                    </BaseButton>
                                ))}
                            </>
                        ) : undefined
                    }
                    onDismiss={store.dismissBanner}
                >
                    {store.banner.body}
                </S.PageBanner>
            )}

            {configTab && <ConfigTab store={store} onEditProfile={setEditing} />}
            {tab === 'profiles' && <ProfilesTab store={store} />}
            {tab === 'keys' &&
                (demo ? (
                    <S.Centered>
                        <S.CenteredTitle>Unavailable in demo mode</S.CenteredTitle>
                        Keys belong to real accounts, so nothing is requested or shown while demo mode is on.
                    </S.Centered>
                ) : (
                    <KeysTab store={store} onRevoke={(k): void => void revokeOne(k)} />
                ))}
            {tab === 'test' &&
                (demo ? (
                    <S.Centered>
                        <S.CenteredTitle>Unavailable in demo mode</S.CenteredTitle>
                        Gateway requests are blocked while demo mode is on.
                    </S.Centered>
                ) : (
                    <TestingTab store={store} onIssue={(): void => setDialog({ kind: 'issue' })} />
                ))}
            {tab === 'catalog' &&
                (demo ? (
                    <S.Centered>
                        <S.CenteredTitle>Unavailable in demo mode</S.CenteredTitle>
                        The gateway catalog is not shown while demo mode is on.
                    </S.Centered>
                ) : (
                    <CatalogTab store={store} />
                ))}

            {editing !== null && store.draft && (
                // Keyed so reopening for another profile starts from that profile, not the previous form.
                <ProfileDrawer
                    key={editing}
                    store={store}
                    index={editing}
                    onClose={(): void => setEditing(null)}
                    onChangedChange={setDrawerChanged}
                />
            )}

            {dialog?.kind === 'issue' && (
                <IssueKeyDialog
                    store={store}
                    onClose={(): void => setDialog(null)}
                    onIssued={(issued): void => setDialog({ kind: 'secret', issued })}
                />
            )}
            {dialog?.kind === 'secret' && (
                <KeySecretDialog
                    issued={dialog.issued}
                    onClose={(): void => setDialog(null)}
                    onOpenTesting={(): void => {
                        setDialog(null);
                        setTab('test');
                    }}
                />
            )}
            {dialog?.kind === 'revoke' && bulkRevoke && (
                <RevokeKeysDialog
                    store={store}
                    initialScope={dialog.scope}
                    onClose={(): void => setDialog(null)}
                    onRevoked={(result): void => {
                        setDialog(null);
                        toast.notify?.(
                            `Revoked ${result.revokedCount} key${result.revokedCount === 1 ? '' : 's'} · cutoff ${formatWhen(result.cutoffAt)}`,
                            'right',
                            'dark',
                        );
                    }}
                />
            )}
            {dialog?.kind === 'confirmEmpty' && (
                <ConfirmEmptyDialog
                    count={dialog.count}
                    onCancel={(): void => setDialog(null)}
                    onConfirm={(): void => {
                        setDialog(null);
                        void store.save({ forceEmpty: true }).then(res => {
                            if (res.ok) toast.notify?.('Configuration saved · effective for new keys', 'right', 'dark');
                        });
                    }}
                />
            )}
        </S.Wrapper>
    );
});
